/**
 * TrashPage — Corbeille des clients (Phase 12).
 *
 * Logique 2 paliers :
 *  1. Client archivé (soft) → ici listé, peut être RESTAURÉ
 *  2. Suppression définitive → confirmation forte "Tapez SUPPRIMER"
 *     puis appel DELETE /api/clients/{id}?hard=true&confirm=SUPPRIMER
 *
 * ⚠️ Hard delete = destruction TOTALE (client + documents + factures + journaux côté DB).
 * Les documents sont archivés ailleurs (responsabilité utilisateur).
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import {
  Trash2, RotateCcw, AlertTriangle, Search, Loader2, X, ShieldAlert,
  FolderOpen, Layers,
} from "lucide-react";
import UniversalArchiveSection from "@/components/admin/UniversalArchiveSection";

export default function TrashPage() {
  const [tab, setTab] = useState("clients"); // clients | universal
  const [universalCount, setUniversalCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [confirmInput, setConfirmInput] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/clients-trash");
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let cancelled = false;
    api.get("/admin/trash/all").then((r) => {
      if (!cancelled) setUniversalCount(r.data?.total || 0);
    }).catch((e) => { console.error("[Trash] initial count failed:", e); });
    return () => { cancelled = true; };
  }, [tab]);

  const filtered = items.filter((c) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (
      (c.first_name || "").toLowerCase().includes(s) ||
      (c.last_name || "").toLowerCase().includes(s) ||
      (c.email || "").toLowerCase().includes(s)
    );
  });

  const restore = async (client) => {
    if (!window.confirm(`Restaurer ${client.first_name} ${client.last_name} ?`)) return;
    setBusy(true);
    try {
      // Batch #13.3 — Route différente selon type (client vs master)
      if (client._trash_type === "master") {
        await api.post(`/masters/${client.id}/restore`);
      } else {
        await api.post(`/clients/${client.id}/restore`);
      }
      toast.success(`${client.first_name || client.last_name || "Élément"} restauré ✓`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const confirmHardDelete = async () => {
    if (!deleteTarget) return;
    if (confirmInput !== "SUPPRIMER") {
      toast.error('Vous devez taper exactement "SUPPRIMER"');
      return;
    }
    setBusy(true);
    try {
      // Batch #13.3 — Route différente selon type
      const r = deleteTarget._trash_type === "master"
        ? await api.delete(`/masters/${deleteTarget.id}/hard?confirm=SUPPRIMER`)
        : await api.delete(`/clients/${deleteTarget.id}?hard=true&confirm=SUPPRIMER`);
      toast.success(
        `${deleteTarget.first_name || deleteTarget.last_name || "Dossier"} supprimé définitivement (${r.data?.cascade_total || 0} entrées effacées)`,
      );
      setDeleteTarget(null);
      setConfirmInput("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-2" data-testid="trash-page">
      <div className="bg-gradient-to-br from-rose-50 to-amber-50 border border-rose-100 rounded-2xl p-5 mb-5">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-rose-100 text-rose-700 flex items-center justify-center">
            <Trash2 className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-800">Corbeille</h1>
            <p className="text-sm text-slate-600 mt-1">
              Dossiers prêts pour la suppression définitive. Vous pouvez encore les restaurer (retour à l'état actif).
              <span className="inline-flex items-center gap-1 ml-2 text-rose-700 font-medium">
                <ShieldAlert className="w-3.5 h-3.5" />
                La suppression définitive est <b>irréversible</b>.
              </span>
            </p>
            <p className="text-[11px] text-slate-500 mt-1 italic">
              Workflow : <b>Actif → Archivage → Corbeille → Suppression définitive</b>
            </p>
          </div>
        </div>
      </div>

      {/* Onglets */}
      <div className="flex gap-2 mb-4 border-b border-slate-200">
        <button
          onClick={() => setTab("clients")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
            tab === "clients" ? "border-rose-500 text-rose-700" : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
          data-testid="trash-tab-clients"
        >
          <FolderOpen className="w-4 h-4 inline mr-1.5" />
          Dossiers clients ({items.length})
        </button>
        <button
          onClick={() => setTab("universal")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
            tab === "universal" ? "border-rose-500 text-rose-700" : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
          data-testid="trash-tab-universal"
        >
          <Layers className="w-4 h-4 inline mr-1.5" />
          Autres items ({universalCount})
        </button>
      </div>

      {/* ═══════════ Onglet UNIVERSEL ═══════════ */}
      {tab === "universal" && (
        <UniversalArchiveSection
          mode="trash"
          onRefresh={() => api.get("/admin/trash/all").then((r) => setUniversalCount(r.data?.total || 0)).catch((e) => { console.error("[Trash] count refresh failed:", e); })}
        />
      )}

      {/* ═══════════ Onglet CLIENTS (legacy) ═══════════ */}
      {tab === "clients" && (<>
      {/* Search */}
      <div className="relative max-w-sm mb-4">
        <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher dans la corbeille..."
          className="pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm w-full"
          data-testid="trash-search"
        />
      </div>

      {/* List */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="inline w-7 h-7 animate-spin text-slate-300" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-sm text-slate-400">
            {items.length === 0
              ? "La corbeille est vide."
              : "Aucun résultat pour cette recherche."}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-3">Dossier</th>
                <th className="text-left px-4 py-3">Mis en corbeille le</th>
                <th className="text-left px-4 py-3">Par</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr
                  key={c.id}
                  className="border-t border-slate-100 hover:bg-slate-50/50"
                  data-testid={`trash-row-${c.id}`}
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-800 inline-flex items-center gap-1.5">
                      {c._trash_type === "master" && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 font-bold uppercase tracking-wider">Couple</span>
                      )}
                      {c.first_name} {c.last_name}
                    </div>
                    {c.email && (
                      <div className="text-[11px] text-slate-500">{c.email}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {(c.trashed_at || c.archived_at)
                      ? new Date(c.trashed_at || c.archived_at).toLocaleString("fr-CA", {
                          dateStyle: "short",
                          timeStyle: "short",
                        })
                      : "—"}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {c.trashed_by || c.archived_by || "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-2">
                      <button
                        onClick={() => restore(c)}
                        disabled={busy}
                        className="text-xs px-2.5 py-1 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 inline-flex items-center gap-1 disabled:opacity-50"
                        data-testid={`trash-restore-${c.id}`}
                      >
                        <RotateCcw className="w-3 h-3" />
                        Restaurer
                      </button>
                      <button
                        onClick={() => {
                          setDeleteTarget(c);
                          setConfirmInput("");
                        }}
                        disabled={busy}
                        className="text-xs px-2.5 py-1 rounded-lg border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 inline-flex items-center gap-1 disabled:opacity-50"
                        data-testid={`trash-hard-delete-${c.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                        Supprimer
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      </>)}

      {/* Hard delete confirmation modal */}
      {deleteTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          data-testid="trash-hard-delete-modal"
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-rose-700" />
                </div>
                <h3 className="font-bold text-slate-800">Suppression définitive</h3>
              </div>
              <button
                onClick={() => {
                  setDeleteTarget(null);
                  setConfirmInput("");
                }}
                className="p-1 rounded hover:bg-slate-100"
              >
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>
            <p className="text-sm text-slate-600 mb-3">
              Vous allez supprimer <b>définitivement</b>{" "}
              <span className="text-rose-700 font-semibold">
                {deleteTarget.first_name} {deleteTarget.last_name}
              </span>{" "}
              ainsi que toutes ses données :
            </p>
            <ul className="text-xs text-slate-500 list-disc pl-5 mb-4 space-y-0.5">
              <li>Documents, factures, dépenses</li>
              <li>Journaux de communication et signatures</li>
              <li>Rendez-vous, mandats, transmissions</li>
              <li>Le master associé sera également supprimé s'il est seul</li>
            </ul>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-xs text-amber-900">
              <ShieldAlert className="w-4 h-4 inline mr-1" />
              Cette action est <b>irréversible</b>. Aucun retour arrière possible.
            </div>
            <label className="block mb-4">
              <span className="text-xs font-medium text-slate-700">
                Pour confirmer, tapez{" "}
                <code className="bg-slate-100 px-1.5 py-0.5 rounded text-rose-700 font-mono">
                  SUPPRIMER
                </code>{" "}
                ci-dessous :
              </span>
              <input
                type="text"
                value={confirmInput}
                onChange={(e) => setConfirmInput(e.target.value)}
                className="mt-1 w-full px-3 py-2 border-2 border-rose-200 rounded-lg text-sm focus:border-rose-500 focus:ring-2 focus:ring-rose-200 outline-none font-mono"
                placeholder="SUPPRIMER"
                autoFocus
                data-testid="trash-confirm-input"
              />
            </label>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setDeleteTarget(null);
                  setConfirmInput("");
                }}
                className="px-4 py-2 text-sm rounded-lg border border-slate-200 hover:bg-slate-50"
                data-testid="trash-confirm-cancel"
              >
                Annuler
              </button>
              <button
                onClick={confirmHardDelete}
                disabled={busy || confirmInput !== "SUPPRIMER"}
                className="px-4 py-2 text-sm rounded-lg bg-rose-600 text-white hover:bg-rose-700 inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="trash-confirm-delete"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                <Trash2 className="w-4 h-4" />
                Supprimer définitivement
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
